import _ from 'lodash';

import {escapeRegex} from '../../../libs/shared/core/src/helpers/utilities/stringHelpers';
import {
  EmojiTextPart,
  emojiToDefinition,
  isValidShortcode,
} from '../../../srcCommon/helpers/emoji/emojiTextHelpers';
import {baseEmojiValidationMap, EmojiMap} from '../../services/emojis/emojis';

// Based off https://github.com/iamcal/js-emoji/blob/0c44c758859b64b4fb1b325b3ef24f1804b40412/lib/emoji.js#L2174
const emoticonsToShortcodeMap: {[emoticon: string]: string | undefined} = {
  '</3': ':broken_heart:',
  '=)': ':smiley:',
  '=-)': ':smiley:',
  'C:': ':smile:',
  'c:': ':smile:',
  ':D': ':smile:',
  ':-D': ':smile:',
  ':>': ':laughing:',
  ':->': ':laughing:',
  ';)': ':wink:',
  ';-)': ':wink:',
  ':|': ':neutral_face:',
  ':-|': ':neutral_face:',
  ':\\': ':confused:',
  ':-\\': ':confused:',
  ':/': ':confused:',
  ':-/': ':confused:',
  ':*': ':kissing_heart:',
  ':-*': ':kissing_heart:',
  ':p': ':stuck_out_tongue:',
  ':-p': ':stuck_out_tongue:',
  ':P': ':stuck_out_tongue:',
  ':-P': ':stuck_out_tongue:',
  ':b': ':stuck_out_tongue:',
  ':-b': ':stuck_out_tongue:',
  ';p': ':stuck_out_tongue_winking_eye:',
  ';-p': ':stuck_out_tongue_winking_eye:',
  ';b': ':stuck_out_tongue_winking_eye:',
  ';-b': ':stuck_out_tongue_winking_eye:',
  ';P': ':stuck_out_tongue_winking_eye:',
  ';-P': ':stuck_out_tongue_winking_eye:',
  // TODO: Fix https://frontjira.atlassian.net/browse/PB-22754
  // '):': ':disappointed:',
  ':(': ':disappointed:',
  ':-(': ':disappointed:',
  '>:(': ':angry:',
  '>:-(': ':angry:',
  ":'(": ':cry:',
  'D:': ':anguished:',
  ':o': ':open_mouth:',
  ':-o': ':open_mouth:',
  ':O': ':open_mouth:',
  ':-O': ':open_mouth:',
  ':)': ':slightly_smiling_face:',
  '(:': ':slightly_smiling_face:',
  ':-)': ':slightly_smiling_face:',
  '<3': ':heart:',
};

const emoticonStrings = Object.keys(emoticonsToShortcodeMap);

const emoticonsRegexParts = emoticonStrings.map((p) => escapeRegex(p)).join('|');

const emoticonsRegex = new RegExp(`(${emoticonsRegexParts})`);

export function isTextEmoticon(text: string) {
  return Boolean(emoticonsToShortcodeMap[text]);
}

export function emoticonTextToShortcode(text: string) {
  return emoticonsToShortcodeMap[text];
}

export function textPartContainsEmoticons(text: string, validationMap: EmojiMap) {
  const isEmoji = isValidShortcode(text, validationMap);
  return emoticonStrings.some((e) => text.includes(e) && !isEmoji);
}

export function splitEmoticonsToEmoji(
  textPartContent: string,
  fullString?: string,
  cursorPosition?: number,
): Array<EmojiTextPart> {
  const emoticonParts = textPartContent.split(emoticonsRegex);
  let offset = 0;

  return _(emoticonParts)
    .map((part) => {
      offset += part.length;

      const isEmoticon = Boolean(emoticonsToShortcodeMap[part]);
      const isSafe = isSafeToMatch(part, fullString || textPartContent, offset, isEmoticon, cursorPosition);
      const shortcode = isEmoticon && isSafe ? emoticonTextToShortcode(part) : undefined;
      // We don't need custom emojis here, since all emoticons map to regular emojis
      const emojiAndSkinTone = shortcode ? emojiToDefinition(shortcode, baseEmojiValidationMap) : undefined;

      return {
        text: part,
        emojiAndSkinTone,
      };
    })
    .compact()
    .value();
}

function isSafeToMatch(
  emoticon: string,
  fullString: string,
  offset: number,
  isEmoticon: boolean,
  cursorPosition?: number,
) {
  if (!isEmoticon) {
    return false;
  }

  // Never render an emoticon to emoji if the cursor is still on it.
  const isEmoticonUnderCursor =
    cursorPosition === undefined
      ? false
      : offset - emoticon.length <= cursorPosition && cursorPosition <= offset;
  if (isEmoticonUnderCursor) {
    return false;
  }

  // Grab the text before and after the emoticon.
  const beforeEmoticon = fullString.substring(0, offset - emoticon.length);
  const afterEmoticon = fullString.slice(offset);
  const hasSpaceAfter = afterEmoticon.match(/^\s/) !== null;
  const isCursorInNode = cursorPosition !== undefined;
  const hasSpaceAfterOrSafelyNothing = hasSpaceAfter || (!isCursorInNode && afterEmoticon === '');
  const hasSpaceBefore = beforeEmoticon.match(/\s$/) !== null;

  // If emoticon is letter + `:`, match only if there's a space before the emoticon.
  if (emoticon.match(/[a-zA-Z]:/)) {
    return hasSpaceBefore;
  }

  // If emoticon is `:` + letter, match only if the text is not under the cursor or if there's a space after the emoticon.
  if (emoticon.match(/:[a-zA-z]/)) {
    return hasSpaceAfterOrSafelyNothing;
  }

  // If the emoticon contains a closing parenthesis, we need to do some checking.
  if (emoticon.includes(')') && !emoticon.includes('(')) {
    // If there's a closing parenthesis after an opening parenthesis (or an opening parenthesis with no closing), we're safe.
    if (afterEmoticon.indexOf(')') > afterEmoticon.indexOf('(')) {
      return hasSpaceAfter;
    }

    // Grab the indices of the last opening parenthesis and the last closing parenthesis.
    // If there is an opening parenthesis after a closing parenthesis, then it means our emoticon closes it so it's not safe.
    if (beforeEmoticon.lastIndexOf('(') > beforeEmoticon.lastIndexOf(')')) {
      return false;
    }
  }

  return hasSpaceAfterOrSafelyNothing;
}
